import { useState, useEffect } from 'react';
import { useTheme } from '../../../context/theme';
import { useToast } from '../../../context/toast';
import { api } from '../../../utils/api';
import { Btn, Inp, Card, EmptyState } from '../../../components/ui';
import { SkeletonList } from '../../../components/ui/Skeletons';

export default function FinancialDashboardTab({ siteId, site, user }) {
  const { theme, isDark } = useTheme();
  const { toast } = useToast();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [carryCosts, setCarryCosts] = useState([]);
  const [lenderData, setLenderData] = useState({ lenders: [], draws: [] });
  const [editBudget, setEditBudget] = useState(false);
  const [budgetForm, setBudgetForm] = useState({});
  const [editSale, setEditSale] = useState(false);
  const [salePrice, setSalePrice] = useState('');
  const [showLender, setShowLender] = useState(false);
  const isAdmin = user.role === 'admin';

  const load = async () => {
    try {
      const s = await api.get('/api/sites/' + siteId + '/financial/summary');
      setSummary(s);
      setBudgetForm(Object.fromEntries((s.lineItems || []).map(l => [l.line_item, l.budget])));
      setSalePrice(s.projectedSale || '');
      const cc = await api.get('/api/sites/' + siteId + '/financial/carry-costs');
      setCarryCosts(cc.carryCosts || []);
      const ld = await api.get('/api/sites/' + siteId + '/financial/lender');
      setLenderData(ld);
      setLoading(false);
    } catch (e) { setLoading(false); }
  };

  useEffect(() => { load(); }, [siteId]);

  const saveBudget = async () => {
    try {
      const items = Object.entries(budgetForm).map(([k, v]) => ({ line_item: k, budget_amount: Number(v) || 0 }));
      await api.put('/api/sites/' + siteId + '/financial/budget', { items });
      toast('Budget saved'); setEditBudget(false); load();
    } catch (e) { toast(e.message, 'error'); }
  };

  const saveSale = async () => {
    try {
      await api.put('/api/sites/' + siteId + '/financial/projected-sale', { projected_sale_price: Number(salePrice) || 0 });
      toast('Projected sale updated'); setEditSale(false); load();
    } catch (e) { toast(e.message, 'error'); }
  };

  const varianceColor = (v) => v > 0 ? '#4ADE80' : v < 0 ? '#EF4444' : 'rgba(255,255,255,0.58)';

  if (loading) return <Card><SkeletonList count={6} /></Card>;
  if (!summary) return <Card><EmptyState icon="dollar-sign" title="No financial data" description="Set budget line items to get started" /></Card>;

  const { lineItems, totals, projectedSale, margin, monthlyCarryCost } = summary;
  const gridHeader = { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr', padding: '8px 12px', background: isDark ? '#2a2a32' : '#f1f5f9', fontSize: 11, fontWeight: 700, color: theme.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5 };
  const inputBudgetStyle = { width: '100%', textAlign: 'right', padding: '4px 8px', background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: 6, color: theme.text, fontSize: 13 };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <a href={'/accounting?site_id=' + siteId} onClick={e => { e.preventDefault(); window.history.pushState({ view: 'accounting' }, '', '/accounting?site_id=' + siteId); window.dispatchEvent(new PopStateEvent('popstate', { state: { view: 'accounting' } })); }} style={{ fontSize: 12, color: theme.accent, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
          View in Accounting →
        </a>
      </div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 400, fontFamily: "'DM Sans', sans-serif", letterSpacing: '-0.01em', color: theme.text, margin: 0 }}>Project Budget</h3>
          {isAdmin && <Btn sm onClick={() => setEditBudget(!editBudget)}>{editBudget ? 'Cancel' : 'Edit Budget'}</Btn>}
        </div>
        <div style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid ' + theme.border }}>
          <div style={gridHeader}>
            <span>Line Item</span><span style={{ textAlign: 'right' }}>Budget</span><span style={{ textAlign: 'right' }}>Committed</span><span style={{ textAlign: 'right' }}>Billed</span><span style={{ textAlign: 'right' }}>Paid</span><span style={{ textAlign: 'right' }}>Variance</span>
          </div>
          {lineItems.map((r, i) => (
            <div key={r.line_item} className="hover-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr', padding: '10px 12px', borderTop: '1px solid ' + theme.border, fontSize: 13, cursor: 'default', background: i % 2 === 0 ? 'transparent' : (isDark ? '#ffffff05' : 'rgba(255,255,255,0.03)') }}>
              <span style={{ fontWeight: 600, color: theme.text }}>{r.line_item}</span>
              {editBudget
                ? <input type="number" value={budgetForm[r.line_item] || ''} onChange={e => setBudgetForm({ ...budgetForm, [r.line_item]: e.target.value })} style={inputBudgetStyle} />
                : <span style={{ textAlign: 'right', color: theme.text, fontWeight: 500 }}>${(r.budget || 0).toLocaleString()}</span>
              }
              <span style={{ textAlign: 'right', color: '#F59E0B', fontWeight: 500 }}>${(r.committed || 0).toLocaleString()}</span>
              <span style={{ textAlign: 'right', color: '#4ADE80', fontWeight: 500 }}>${(r.billed || 0).toLocaleString()}</span>
              <span style={{ textAlign: 'right', color: '#4ADE80', fontWeight: 500 }}>${(r.paid || 0).toLocaleString()}</span>
              <span style={{ textAlign: 'right', color: varianceColor(r.variance), fontWeight: 700 }}>{(r.variance >= 0 ? '+$' : '-$') + Math.abs(r.variance || 0).toLocaleString()}</span>
            </div>
          ))}
          {/* Totals row */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr', padding: '10px 12px', borderTop: '2px solid ' + theme.border, fontSize: 13, fontWeight: 700, background: isDark ? '#1a1a20' : '#e2e8f0' }}>
            <span style={{ color: theme.text }}>Total Investment</span>
            <span style={{ textAlign: 'right', color: theme.text }}>${(totals.budget || 0).toLocaleString()}</span>
            <span style={{ textAlign: 'right', color: '#F59E0B' }}>${(totals.committed || 0).toLocaleString()}</span>
            <span style={{ textAlign: 'right', color: '#4ADE80' }}>${(totals.billed || 0).toLocaleString()}</span>
            <span style={{ textAlign: 'right', color: '#4ADE80' }}>${(totals.paid || 0).toLocaleString()}</span>
            <span></span>
          </div>
          {/* Projected Sale */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 5fr', padding: '10px 12px', borderTop: '1px solid ' + theme.border, fontSize: 13 }}>
            <span style={{ fontWeight: 600, color: theme.text }}>Projected Sale to 5C</span>
            {editSale
              ? <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}><input type="number" value={salePrice} onChange={e => setSalePrice(e.target.value)} style={{ width: 140, textAlign: 'right', padding: '4px 8px', background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: 6, color: theme.text, fontSize: 13 }} /><Btn sm primary onClick={saveSale}>Save</Btn></div>
              : <span style={{ textAlign: 'right', color: theme.text, fontWeight: 600, cursor: isAdmin ? 'pointer' : 'default' }} onClick={() => isAdmin && setEditSale(true)}>${(projectedSale || 0).toLocaleString()}{isAdmin && ' ✏️'}</span>
            }
          </div>
          {/* Margin */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 5fr', padding: '12px', borderTop: '2px solid ' + (margin >= 0 ? '#4ADE80' : '#EF4444'), fontSize: 16, fontWeight: 800 }}>
            <span style={{ color: theme.text }}>DLP Margin</span>
            <span style={{ textAlign: 'right', color: margin >= 0 ? '#4ADE80' : '#EF4444' }}>{(margin >= 0 ? '+$' : '-$') + Math.abs(margin || 0).toLocaleString()}</span>
          </div>
        </div>
        {editBudget && <Btn sm primary onClick={saveBudget} style={{ marginTop: 10 }}>Save Budget</Btn>}
      </Card>

      {/* Carry Costs */}
      <Card style={{ marginTop: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 400, fontFamily: "'DM Sans', sans-serif", letterSpacing: '-0.01em', color: theme.text, margin: '0 0 12px' }}>
          Carry Costs<span style={{ color: theme.textSecondary, fontSize: 12, marginLeft: 8 }}>${monthlyCarryCost.toLocaleString()}/mo</span>
        </h3>
        {carryCosts.length === 0
          ? <p style={{ color: theme.textSecondary, fontSize: 13 }}>No carry costs recorded</p>
          : carryCosts.map(cc => (
            <div key={cc.id} className="hover-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid ' + theme.border, fontSize: 13, cursor: 'default' }}>
              <span style={{ color: theme.text }}>{cc.cost_type}{cc.label ? ' — ' + cc.label : ''}</span>
              <span style={{ fontWeight: 600, color: '#F59E0B' }}>${Number(cc.monthly_amount || 0).toLocaleString()}/mo</span>
            </div>
          ))
        }
      </Card>

      {/* Lender / Funding Tracker */}
      {lenderData.lenders.length > 0 && (
        <Card style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', marginBottom: showLender ? 12 : 0 }} onClick={() => setShowLender(!showLender)}>
            <h3 style={{ fontSize: 16, fontWeight: 400, fontFamily: "'DM Sans', sans-serif", letterSpacing: '-0.01em', color: theme.text, margin: 0 }}>{showLender ? '▾' : '▸'} Lender / Funding Tracker</h3>
            <span style={{ fontSize: 12, color: theme.textSecondary }}>{lenderData.lenders.length} source(s)</span>
          </div>
          {showLender && lenderData.lenders.map(l => (
            <div key={l.id} style={{ padding: 12, background: isDark ? '#1e1e24' : '#f8f9fa', borderRadius: 8, marginBottom: 8, border: '1px solid ' + theme.border }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontWeight: 700, color: theme.text }}>{l.funding_source}</span>
                <span style={{ color: '#4ADE80', fontWeight: 600 }}>${Number(l.loan_amount || 0).toLocaleString()}{l.interest_rate ? ' @ ' + l.interest_rate + '%' : ''}</span>
              </div>
              {lenderData.draws.filter(d => d.lender_id === l.id).map(d => (
                <div key={d.id} className="hover-row" style={{ display: 'grid', gridTemplateColumns: '60px 1fr 1fr 1fr 1fr', fontSize: 12, padding: '4px 0', borderTop: '1px solid ' + theme.border, color: theme.textSecondary, cursor: 'default' }}>
                  <span>Draw #{d.draw_number}</span>
                  <span>{d.date_requested ? new Date(d.date_requested).toLocaleDateString() : '—'}</span>
                  <span>${Number(d.amount).toLocaleString()}</span>
                  <span>{d.date_funded ? new Date(d.date_funded).toLocaleDateString() : 'Pending'}</span>
                  <span>{d.balance ? 'Bal: $' + Number(d.balance).toLocaleString() : ''}</span>
                </div>
              ))}
            </div>
          ))}
        </Card>
      )}
    </>
  );
}
